/* eslint-disable import/prefer-default-export */
/**
 * PostHog feature-flag getters — the counterpart to growthbook.js.
 *
 * These historically lived in growthbook.js (some flags were migrated off
 * GrowthBook to PostHog with their keys unchanged), which made the file name
 * misleading. They read synchronously through PosthogService so call sites can
 * stay non-reactive; the flag keys themselves live in constants/ff.posthog.js.
 */
import { PosthogService } from './services/posthog';
import { FeatureFlagKeys } from './constants/ff.posthog';

/**
 * Gates the Best in Dental cycle admin: its two routes and its rail entry.
 *
 * Read synchronously because App.js decides which routes to register during
 * render — an async read would leave a deep link to fall through to the
 * catch-all redirect before the flag arrived.
 *
 * Visibility only. awards-service gates the API on ADMIN_ALLOWLIST_USER_IDS, so
 * an admin with the flag on but not on the allowlist still gets 403s.
 */
export const getShouldEnableBestInDental = () =>
  PosthogService.getInstance().isFeatureEnabled(FeatureFlagKeys.bestInDentalAdminPanel, false);

/**
 * Gates the GoTu Admin list: its route and its rail entry.
 *
 * Read synchronously, matching the Best in Dental gate above — App.js decides
 * which routes to register during render, so an async read would leave a deep
 * link to fall through to the catch-all redirect before the flag arrived.
 *
 * Visibility only. `GET /auth-service/admin/internal-users` is already gated by
 * AdminAuthMiddleware, so the flag decides who sees the page, not who may read
 * the data — every admin can call that endpoint today with or without it.
 */
export const getShouldEnableGotuAdmins = () =>
  PosthogService.getInstance().isFeatureEnabled(FeatureFlagKeys.gotuAdminsAdminPanel, false);

/**
 * Gates the Sales Metros page (GOTU-6009): its route and its rail entry.
 *
 * Same key as `getShouldSourceRamsFromSalesMetros` below — the page and the RAM
 * lists' switch to sales metros ship together — but read at render, like the
 * route gates above, rather than once at boot. Forcing the flag mid-session
 * therefore shows the page at once, while the RAM lists change only on reload.
 *
 * Visibility only. organization-service guards the reads with admin auth and
 * the assignment write with its own claim, flag or no flag.
 */
export const getShouldEnableSalesMetrosPage = () =>
  PosthogService.getInstance().isFeatureEnabled(FeatureFlagKeys.salesMetrosAdminPanel, false);

/**
 * Gates the Location Users list, the Location User profile at
 * `/location-users/:identityId` and its tab routes, plus their rail entry.
 *
 * Read synchronously for the same reason as the two gates above: App.js
 * decides which routes to register during render, so an async read would send
 * a deep link to a Location User through the catch-all redirect.
 *
 * Visibility only. The auth-service, organization-service and
 * notification-sender routes the list and the profile read are behind admin
 * auth already.
 */
export const getShouldEnableLocationUsers = () =>
  PosthogService.getInstance().isFeatureEnabled(FeatureFlagKeys.locationUsersAdminPanel, false);

/**
 * Chooses where the DSO manager list comes from: on, the auth-service role
 * query; off, the Firebase-backed `dsoManagers` field of the monolith's
 * `admin/rams-dsomanagers`.
 *
 * Read only by `ensureManagerListSources`, which records the answer once per
 * session so the two sources stay behind one writer of `state.users.dsoManagers`.
 * UI releases here ride a numbered release train, so a bad role table in prod
 * needs a flag flip rather than a revert.
 *
 * Defaults off. Remove alongside the Firebase DSO_MANAGERS cleanup (GOTU-6387
 * follow-up), which is also when the fallback branch goes.
 */
export const getShouldSourceDsoManagersFromAuthService = () =>
  PosthogService.getInstance().isFeatureEnabled(FeatureFlagKeys.dsoManagersFromAuthService, false);

/**
 * Chooses where the recruiter list comes from: on, the auth-service role query;
 * off, the hand-edited `PERMANENT_RECRUITER_IDS` Remote Config list resolved
 * through `useGetUsersByIds`. Sibling of the DSO manager flag above.
 *
 * This one needs its own flag rather than riding that one. The `recruiter` role
 * is newer than `dso` — product split "Recruiting / AE" into two roles — so an
 * unpopulated or half-populated role table is likelier here, and the three
 * consuming surfaces would degrade to a dropdown of only "All" with no error.
 * On the release train that is a revert plus a train, not a flip.
 *
 * Read through a module getter for symmetry with the DSO flag, even though
 * these callers are components; see `useGetRecruiters`.
 *
 * Defaults off. Remove alongside the Firebase PERMANENT_RECRUITER_IDS cleanup,
 * which is when `useGetUsersByIds` and `getPermanentRecruiterIds` go too.
 */
export const getShouldSourceRecruitersFromAuthService = () =>
  PosthogService.getInstance().isFeatureEnabled(FeatureFlagKeys.recruitersFromAuthService, false);

/**
 * Chooses where the RAM list comes from: on, organization-service's sales
 * metros; off, the `ramUsers` field of the monolith's `admin/rams-dsomanagers`.
 * On also makes the office profile's RAM field read-only, since sales metros
 * are auto-assigned from the office's zip code.
 *
 * One flag for every surface that reads `state.users.ramUsers`, so the filters
 * and the profile field cannot disagree about which list is real. This getter
 * is read only by `ensureManagerListSources`, which records the answer once per
 * session; the Sales Metros page reads the same key at render through
 * `getShouldEnableSalesMetrosPage`.
 *
 * Defaults off. Must be fully on before the monolith stops serving `ramUsers`
 * (GOTU-7544), which is the off branch's only source.
 */
export const getShouldSourceRamsFromSalesMetros = () =>
  PosthogService.getInstance().isFeatureEnabled(FeatureFlagKeys.salesMetrosAdminPanel, false);

/**
 * Chooses how the RAM filters spell "no metro" while the RAM list comes from
 * sales metros: on, as the reserved Unassigned metro's all-zeros id, like any
 * other metro; off, as the `'unassigned'` sentinel the monolith has always
 * parsed. Off also drops the reserved row from the list, so there is one
 * Unassigned option either way.
 *
 * The monolith owns this key (tempmee_server, GOTU-7543) and decides from it
 * whether an office in no metro stores the zero id and whether a `ram` filter
 * matches it. The portal only mirrors it, so that a zero id never goes to a
 * monolith that would match it literally. The two sides agree only while the
 * flag is targeted per environment or for everyone: the monolith evaluates it
 * as `distinct_id: 'backend'`, the portal as the signed-in admin, so a
 * percentage or person-property rollout would split them.
 *
 * Read only by `ensureManagerListSources`, once per session. Turn
 * `GOTU-6007-sales-metros-admin-panel` on before this in every environment.
 * Defaults off. Remove with the admin-panel flag (GOTU-9814); the PostHog key
 * itself goes only once the monolith stops reading it too.
 */
export const getShouldSendUnassignedAsSalesMetro = () =>
  PosthogService.getInstance().isFeatureEnabled(FeatureFlagKeys.salesMetrosSwitch, false);
